import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { ArrowUpTrayIcon, PencilSquareIcon, TrashIcon } from '@heroicons/react/24/outline'
import { toast } from 'sonner'
import Button from '../ui/Button'
import ConfirmDialog from '../ui/ConfirmDialog'
import ImageFullscreenViewer from '../ui/ImageFullscreenViewer'
import {
  createPassSequenceImageUploadUrl,
  deletePassSequenceImage,
  listPassSequenceImagesAdmin,
  registerPassSequenceImage,
  type PassSequenceImageAdminItem,
  type RootType
} from '../../utils/passSequenceImagesClient'
import { putFileToPresignedUrl } from '../../utils/reportsClient'

const rootTypeOptions: Array<{ value: RootType; label: string }> = [
  { value: 'external', label: 'External Root' },
  { value: 'internal', label: 'Internal Root' }
]

function formatFileSize(bytes: number): string {
  if (!Number.isFinite(bytes) || bytes <= 0) return '0 KB'
  if (bytes < 1024 * 1024) return `${Math.max(1, Math.round(bytes / 1024))} KB`
  return `${(bytes / (1024 * 1024)).toFixed(2)} MB`
}

function formatDate(iso: string): string {
  if (!iso) return '—'
  try {
    return new Date(iso).toLocaleString()
  } catch {
    return iso
  }
}

type FormModalProps = {
  open: boolean
  mode: 'create' | 'edit'
  editRow: PassSequenceImageAdminItem | null
  onClose: () => void
  onSaved: () => void
}

function PassSequenceImageFormModal({ open, mode, editRow, onClose, onSaved }: FormModalProps) {
  const fileInputRef = useRef<HTMLInputElement>(null)
  const [file, setFile] = useState<File | null>(null)
  const [rootType, setRootType] = useState<RootType>('external')
  const [capCount, setCapCount] = useState('1')
  const [fillCount, setFillCount] = useState('1')
  const [uploading, setUploading] = useState(false)

  useEffect(() => {
    if (!open) return
    if (mode === 'edit' && editRow) {
      setRootType(editRow.rootType)
      setCapCount(String(editRow.capCount))
      setFillCount(String(editRow.fillCount))
    } else {
      setRootType('external')
      setCapCount('1')
      setFillCount('1')
    }
    setFile(null)
    if (fileInputRef.current) fileInputRef.current.value = ''
  }, [open, mode, editRow])

  const parsedCapCount = Number(capCount)
  const parsedFillCount = Number(fillCount)
  const keysValid =
    Number.isInteger(parsedCapCount) &&
    parsedCapCount >= 1 &&
    parsedCapCount <= 2 &&
    Number.isInteger(parsedFillCount) &&
    parsedFillCount >= 1 &&
    parsedFillCount <= 21
  const canReuseImage =
    mode === 'edit' &&
    editRow &&
    !!editRow.fileKey &&
    !!editRow.imageUrl
  const canSubmit =
    !uploading &&
    keysValid &&
    (mode === 'create' ? !!file : !!file || canReuseImage)

  const previewLabel = useMemo(
    () => `${rootType} / Cap ${capCount} / Fill ${fillCount}`,
    [rootType, capCount, fillCount]
  )

  const handleSubmit = async () => {
    if (!canSubmit) {
      toast.error('Choose valid Root / Cap / Fill. Create requires a new image file.')
      return
    }
    setUploading(true)
    try {
      let fileKey = ''
      let imageUrl = ''
      if (file) {
        const up = await createPassSequenceImageUploadUrl({
          fileName: file.name,
          contentType: file.type || 'image/png'
        })
        await putFileToPresignedUrl(up.uploadUrl, file)
        fileKey = up.fileKey
        imageUrl = up.publicUrl
      } else if (canReuseImage && editRow) {
        fileKey = editRow.fileKey
        imageUrl = editRow.imageUrl
      } else {
        toast.error('Select an image file.')
        setUploading(false)
        return
      }
      if (mode === 'edit' && editRow) {
        const keyChanged =
          editRow.rootType !== rootType ||
          editRow.capCount !== parsedCapCount ||
          editRow.fillCount !== parsedFillCount
        if (keyChanged) {
          await deletePassSequenceImage(editRow.id)
        }
      }
      await registerPassSequenceImage({
        rootType,
        capCount: parsedCapCount,
        fillCount: parsedFillCount,
        imageUrl,
        fileKey
      })
      toast.success(mode === 'edit' ? 'Mapping updated.' : 'Mapping created.')
      onSaved()
      onClose()
    } catch (err: any) {
      toast.error(err?.message || 'Failed to save mapping.')
    } finally {
      setUploading(false)
    }
  }

  if (!open) return null

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4"
      role="dialog"
      aria-modal="true"
      aria-labelledby="pass-seq-modal-title"
      onClick={onClose}
    >
      <div
        className="max-h-[90vh] w-full max-w-2xl overflow-y-auto rounded-lg border border-gray-200 bg-white shadow-xl"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="border-b border-gray-200 px-4 py-3">
          <h2 id="pass-seq-modal-title" className="text-sm font-semibold text-gray-900">
            {mode === 'edit' ? 'Edit pass sequence image' : 'Create pass sequence image'}
          </h2>
        </div>
        <div className="grid gap-4 p-4 lg:grid-cols-2">
          <div>
            <p className="text-sm font-medium text-gray-800">Diagram image</p>
            <input
              ref={fileInputRef}
              type="file"
              accept="image/*"
              className="sr-only"
              disabled={uploading}
              onChange={(event) => {
                setFile(event.target.files?.[0] ?? null)
              }}
            />
            <button
              type="button"
              disabled={uploading}
              onClick={() => fileInputRef.current?.click()}
              className="mt-2 flex w-full flex-col items-center justify-center rounded-xl border-2 border-dashed border-gray-200 bg-gray-50/50 px-6 py-8 text-center transition hover:border-primary-300 hover:bg-primary-50/30 disabled:cursor-not-allowed disabled:opacity-50"
            >
              <ArrowUpTrayIcon className="h-7 w-7 text-gray-400" />
              {file ? (
                <>
                  <span className="mt-2 max-w-full truncate px-2 text-xs font-semibold text-gray-800" title={file.name}>
                    {file.name}
                  </span>
                  <span className="mt-1 text-[11px] text-gray-500">{formatFileSize(file.size)}</span>
                </>
              ) : (
                <span className="mt-2 text-xs font-medium text-gray-700">
                  {mode === 'edit' && canReuseImage
                    ? 'Optional: select a new image, or save to keep the current file'
                    : 'Select diagram image'}
                </span>
              )}
            </button>
          </div>
          <div className="space-y-3">
            <div>
              <label className="text-sm font-medium text-gray-800">Root type</label>
              <select
                className="input-primary mt-1"
                value={rootType}
                onChange={(event) => setRootType(event.target.value as RootType)}
                disabled={uploading}
              >
                {rootTypeOptions.map((option) => (
                  <option key={option.value} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </select>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="text-sm font-medium text-gray-800">Cap count</label>
                <select
                  className="input-primary mt-1"
                  value={capCount}
                  onChange={(event) => setCapCount(event.target.value)}
                  disabled={uploading}
                >
                  <option value="1">1</option>
                  <option value="2">2</option>
                </select>
              </div>
              <div>
                <label className="text-sm font-medium text-gray-800">Fill count</label>
                <select
                  className="input-primary mt-1"
                  value={fillCount}
                  onChange={(event) => setFillCount(event.target.value)}
                  disabled={uploading}
                >
                  {Array.from({ length: 21 }, (_, i) => i + 1).map((value) => (
                    <option key={value} value={String(value)}>
                      {value}
                    </option>
                  ))}
                </select>
              </div>
            </div>
            <div className="rounded-lg border border-gray-200 bg-gray-50 p-2">
              <p className="text-[11px] font-medium text-gray-600">Preview</p>
              <p className="mt-0.5 text-xs text-gray-800">{previewLabel}</p>
            </div>
          </div>
        </div>
        <div className="flex justify-end gap-2 border-t border-gray-200 px-4 py-3">
          <Button variant="secondary" type="button" disabled={uploading} onClick={onClose}>
            Cancel
          </Button>
          <Button type="button" disabled={!canSubmit} onClick={() => void handleSubmit()}>
            {uploading ? 'Saving…' : mode === 'edit' ? 'Save changes' : 'Create'}
          </Button>
        </div>
      </div>
    </div>
  )
}

type PassRootFilter = 'all' | RootType
type PassCapFilter = 'all' | '1' | '2'

export default function PassSequenceImagesAdmin() {
  const [items, setItems] = useState<PassSequenceImageAdminItem[]>([])
  const [listLoading, setListLoading] = useState(true)
  const [filterRoot, setFilterRoot] = useState<PassRootFilter>('all')
  const [filterCap, setFilterCap] = useState<PassCapFilter>('all')
  const [filterFill, setFilterFill] = useState<string>('all')
  const [modalOpen, setModalOpen] = useState(false)
  const [modalMode, setModalMode] = useState<'create' | 'edit'>('create')
  const [editRow, setEditRow] = useState<PassSequenceImageAdminItem | null>(null)
  const [fullscreen, setFullscreen] = useState<{ url: string; title: string } | null>(null)
  const [deleteTarget, setDeleteTarget] = useState<PassSequenceImageAdminItem | null>(null)
  const [deleteBusy, setDeleteBusy] = useState(false)

  const loadList = useCallback(async () => {
    setListLoading(true)
    try {
      const res = await listPassSequenceImagesAdmin()
      setItems(res.items)
    } catch (err: any) {
      toast.error(err?.message || 'Failed to load mappings.')
    } finally {
      setListLoading(false)
    }
  }, [])

  useEffect(() => {
    void loadList()
  }, [loadList])

  const filteredItems = useMemo(() => {
    return items.filter((row) => {
      if (filterRoot !== 'all' && row.rootType !== filterRoot) return false
      if (filterCap !== 'all' && String(row.capCount) !== filterCap) return false
      if (filterFill !== 'all' && String(row.fillCount) !== filterFill) return false
      return true
    })
  }, [items, filterRoot, filterCap, filterFill])

  const clearFilters = () => {
    setFilterRoot('all')
    setFilterCap('all')
    setFilterFill('all')
  }

  const openCreate = () => {
    setModalMode('create')
    setEditRow(null)
    setModalOpen(true)
  }

  const openEdit = (row: PassSequenceImageAdminItem) => {
    setModalMode('edit')
    setEditRow(row)
    setModalOpen(true)
  }

  const confirmDelete = async () => {
    if (!deleteTarget) return
    setDeleteBusy(true)
    try {
      await deletePassSequenceImage(deleteTarget.id)
      toast.success('Mapping deleted.')
      setDeleteTarget(null)
      void loadList()
    } catch (err: any) {
      toast.error(err?.message || 'Delete failed.')
    } finally {
      setDeleteBusy(false)
    }
  }

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <div>
          <h3 className="text-sm font-semibold text-gray-900">Pass sequence image mappings</h3>
          {/* <p className="mt-0.5 text-xs text-gray-500">
            Table of Root + Cap + Fill diagrams. Use filters to narrow rows; edit can keep the same image file.
          </p> */}
        </div>
        <div className="flex gap-2">
          <Button variant="secondary" type="button" disabled={listLoading} onClick={() => void loadList()}>
            Refresh
          </Button>
          <Button type="button" onClick={openCreate}>
            Create
          </Button>
        </div>
      </div>

      <div className="flex flex-wrap items-end gap-3 rounded-lg border border-gray-200 bg-gray-50/90 p-3">
        <div>
          <label className="block text-xs font-medium text-gray-700">Root</label>
          <select
            className="input-primary mt-1 min-w-[140px] text-sm"
            value={filterRoot}
            onChange={(e) => setFilterRoot(e.target.value as PassRootFilter)}
          >
            <option value="all">All roots</option>
            {rootTypeOptions.map((o) => (
              <option key={o.value} value={o.value}>
                {o.label}
              </option>
            ))}
          </select>
        </div>
        <div>
          <label className="block text-xs font-medium text-gray-700">Cap</label>
          <select
            className="input-primary mt-1 min-w-[100px] text-sm"
            value={filterCap}
            onChange={(e) => setFilterCap(e.target.value as PassCapFilter)}
          >
            <option value="all">All</option>
            <option value="1">1</option>
            <option value="2">2</option>
          </select>
        </div>
        <div>
          <label className="block text-xs font-medium text-gray-700">Fill</label>
          <select
            className="input-primary mt-1 min-w-[100px] text-sm"
            value={filterFill}
            onChange={(e) => setFilterFill(e.target.value)}
          >
            <option value="all">All</option>
            {Array.from({ length: 21 }, (_, i) => i + 1).map((n) => (
              <option key={n} value={String(n)}>
                {n}
              </option>
            ))}
          </select>
        </div>
        <Button variant="secondary" type="button" className="mb-0.5" onClick={clearFilters}>
          Clear filters
        </Button>
      </div>

      <div className="overflow-x-auto rounded-lg border border-gray-200">
        <table className="min-w-full divide-y divide-gray-200 text-sm">
          <thead className="sticky top-0 bg-gray-50">
            <tr>
              <th className="px-3 py-2 text-left text-xs font-semibold text-gray-600">Image</th>
              <th className="px-3 py-2 text-left text-xs font-semibold text-gray-600">Root</th>
              <th className="px-3 py-2 text-left text-xs font-semibold text-gray-600">Cap</th>
              <th className="px-3 py-2 text-left text-xs font-semibold text-gray-600">Fill</th>
              <th className="px-3 py-2 text-left text-xs font-semibold text-gray-600">Updated</th>
              <th className="px-3 py-2 text-right text-xs font-semibold text-gray-600">Actions</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-100 bg-white">
            {listLoading ? (
              <tr>
                <td colSpan={6} className="px-3 py-8 text-center text-xs text-gray-500">
                  Loading…
                </td>
              </tr>
            ) : items.length === 0 ? (
              <tr>
                <td colSpan={6} className="px-3 py-8 text-center text-xs text-gray-500">
                  No mappings yet. Use Create to add one.
                </td>
              </tr>
            ) : filteredItems.length === 0 ? (
              <tr>
                <td colSpan={6} className="px-3 py-8 text-center text-xs text-gray-500">
                  No rows match the current filters. Try clearing filters.
                </td>
              </tr>
            ) : (
              filteredItems.map((row) => (
                <tr key={row.id}>
                  <td className="px-3 py-2">
                    {row.signedImageUrl ? (
                      <button
                        type="button"
                        className="block h-14 w-14 overflow-hidden rounded-md border border-gray-200 bg-gray-50 focus:outline-none focus:ring-2 focus:ring-primary-500"
                        onClick={() =>
                          setFullscreen({
                            url: row.signedImageUrl!,
                            title: `${row.rootType} · Cap ${row.capCount} · Fill ${row.fillCount}`
                          })
                        }
                      >
                        <img
                          src={row.signedImageUrl}
                          alt=""
                          className="h-full w-full object-cover"
                        />
                      </button>
                    ) : (
                      <span className="text-xs text-gray-400">No preview</span>
                    )}
                  </td>
                  <td className="px-3 py-2 text-gray-800">{row.rootType}</td>
                  <td className="px-3 py-2 text-gray-800">{row.capCount}</td>
                  <td className="px-3 py-2 text-gray-800">{row.fillCount}</td>
                  <td className="whitespace-nowrap px-3 py-2 text-xs text-gray-600">{formatDate(row.updatedAt)}</td>
                  <td className="whitespace-nowrap px-3 py-2 text-right">
                    <button
                      type="button"
                      className="mr-2 inline-flex rounded p-1 text-primary-600 hover:bg-primary-50"
                      title="Edit"
                      onClick={() => openEdit(row)}
                    >
                      <PencilSquareIcon className="h-5 w-5" />
                    </button>
                    <button
                      type="button"
                      className="inline-flex rounded p-1 text-red-600 hover:bg-red-50"
                      title="Delete"
                      onClick={() => setDeleteTarget(row)}
                    >
                      <TrashIcon className="h-5 w-5" />
                    </button>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      <PassSequenceImageFormModal
        open={modalOpen}
        mode={modalMode}
        editRow={editRow}
        onClose={() => setModalOpen(false)}
        onSaved={() => void loadList()}
      />

      <ImageFullscreenViewer
        open={!!fullscreen}
        url={fullscreen?.url ?? null}
        title={fullscreen?.title}
        onClose={() => setFullscreen(null)}
      />

      <ConfirmDialog
        open={!!deleteTarget}
        title="Delete mapping?"
        description={
          deleteTarget
            ? `Remove diagram for ${deleteTarget.rootType}, Cap ${deleteTarget.capCount}, Fill ${deleteTarget.fillCount}?`
            : ''
        }
        confirmLabel="Delete"
        tone="danger"
        confirmBusy={deleteBusy}
        onCancel={() => !deleteBusy && setDeleteTarget(null)}
        onConfirm={() => void confirmDelete()}
      />
    </div>
  )
}
